import { useEffect, useState } from 'react'
import { inBasket, type CatalogItem, type TripItem } from '@sp/shared'
import { api } from '../../lib/api.ts'
import { productName } from '../../lib/barcode.ts'
import { formatQty } from '../../lib/format.ts'
import { HttpError } from '../../lib/http.ts'
import { errorMessage } from '../../lib/useResource.ts'
import { Sheet } from '../../components/Sheet.tsx'
import { Icon } from '../../components/Icon.tsx'
import { Paragraph, TextCol } from '../../components/ui.tsx'

/** Jak dlouho cekat na nazev z Open Food Facts, nez to vzdat. */
const PRODUCT_TIMEOUT_MS = 5000

interface Props {
  code: string | null
  items: readonly TripItem[]
  /** Nearchivovane polozky sablony. */
  catalog: readonly CatalogItem[]
  onClose(): void
  onLinked(updated: CatalogItem, tripItemId: string): void
}

/** Kod, ktery zadna polozka na seznamu nema: ke ktere patri? Priste se najde sama. */
export function ScanLinkSheet({ code, onClose, ...rest }: Props) {
  return (
    <Sheet open={code !== null} onClose={onClose} title="Neznámý kód" subtitle={code ?? undefined}>
      {code && <LinkBody key={code} code={code} {...rest} />}
    </Sheet>
  )
}

function LinkBody({ code, items, catalog, onLinked }: Omit<Props, 'onClose'> & { code: string }) {
  const [product, setProduct] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    const timer = window.setTimeout(() => ctrl.abort(), PRODUCT_TIMEOUT_MS)
    void productName(code, ctrl.signal).then((name) => {
      if (!ctrl.signal.aborted) setProduct(name)
    })
    return () => {
      window.clearTimeout(timer)
      ctrl.abort()
    }
  }, [code])

  const byId = new Map(catalog.map((ci) => [ci.id, ci]))
  const known = catalog.filter((ci) => ci.barcodes.includes(code))
  // Kod se uklada k polozce sablony, takze prirazeni jde jen u polozek z ni.
  const candidates = items.filter((it) => it.catalogItemId !== null && byId.has(it.catalogItemId) && !inBasket(it))

  async function link(item: TripItem) {
    const ci = item.catalogItemId ? byId.get(item.catalogItemId) : undefined
    if (!ci) return
    setBusy(true)
    setError(null)
    try {
      // Kod se pridava na serveru — neaktualni sablona v telefonu tak neprepise cizi kody.
      const updated = await api.catalog.addBarcode(ci.id, code)
      onLinked(updated, item.id)
    } catch (err) {
      setError(
        err instanceof HttpError && err.isOffline
          ? 'Kód jde uložit jen se signálem. Cenu zatím zapiš přes položku v seznamu.'
          : errorMessage(err),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="stack">
      {product && <Paragraph tone="fg">{`Podle Open Food Facts: ${product}`}</Paragraph>}
      {known.length > 0 && (
        <Paragraph>{`Kód patří k „${known.map((k) => k.name).join('“, „')}“ ze šablony, na seznamu to ale není.`}</Paragraph>
      )}
      {candidates.length > 0 ? (
        <>
          <Paragraph>Ke které položce ze seznamu patří? Příště se najde sama.</Paragraph>
          <div className="store-picks">
            {candidates.map((it) => (
              <button key={it.id} type="button" className="store-pick" disabled={busy} onClick={() => void link(it)}>
                <TextCol title={it.name} sub={formatQty(it.qty, it.unit)} />
                <Icon name="chevron_right" size={18} className="tone-muted" />
              </button>
            ))}
          </div>
        </>
      ) : (
        <Paragraph>Na seznamu nezbývá nic ze šablony, k čemu by šel kód přiřadit.</Paragraph>
      )}
      {error && <Paragraph tone="error">{error}</Paragraph>}
    </div>
  )
}
